/**
 * Zod schémy pre obsah. Slúžia dvom veciam:
 *   1. kontrola JSON súborov pri štarte (chyba v dátach padne hneď, nie v hre),
 *   2. validácia máp importovaných z editora / od používateľa.
 */

import { z } from 'zod';

export const zoneSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});

export const spaceSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  x: z.number(),
  y: z.number(),
  r: z.number().positive(),
  poly: z.array(z.tuple([z.number(), z.number()])).optional(),
  zones: z.array(z.string().min(1)).min(1),
  start: z.number().int().positive().optional(),
});

export const mapSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    image: z.string().optional(),
    w: z.number().positive(),
    h: z.number().positive(),
    zones: z.array(zoneSchema).min(1),
    spaces: z.array(spaceSchema).min(2),
    edges: z.array(z.tuple([z.string(), z.string()])),
    /** Tajné chodby — skupiny priestorov susedných len pre pohyb (Baskerville Manor). */
    passages: z.array(z.array(z.string()).min(2)).optional(),
  })
  .superRefine((map, ctx) => {
    const ids = new Set(map.spaces.map((s) => s.id));
    if (ids.size !== map.spaces.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Duplicitné ID priestoru' });
    }

    for (const group of map.passages ?? []) {
      for (const id of group) {
        if (!ids.has(id)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Tajná chodba odkazuje na neznámy priestor ${id}` });
      }
    }

    const zoneIds = new Set(map.zones.map((z) => z.id));
    for (const space of map.spaces) {
      for (const zone of space.zones) {
        if (!zoneIds.has(zone)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Priestor ${space.id} odkazuje na neznámu zónu ${zone}` });
        }
      }
    }

    // Štartové priestory musia byť očíslované 1..N bez dier — inak sa hra nerozostaví.
    const starts = map.spaces.filter((s) => s.start !== undefined).map((s) => s.start!);
    const unique = new Set(starts);
    if (unique.size !== starts.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Dva priestory majú rovnaké štartové číslo' });
    }
    if (starts.length < 2) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Mapa potrebuje aspoň 2 štartové priestory' });
    }
    for (let i = 1; i <= starts.length; i++) {
      if (!unique.has(i)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Chýba štartový priestor ${i}` });
      }
    }

    for (const [a, b] of map.edges) {
      if (!ids.has(a) || !ids.has(b)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Hrana ${a}–${b} vedie do neznámeho priestoru` });
      }
      if (a === b) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Slučka na priestore ${a}` });
      }
    }
  });

export const cardSchema = z.object({
  id: z.string().min(1),
  deckId: z.string().min(1),
  name: z.string(),
  character: z.string().nullable(),
  type: z.enum(['attack', 'defense', 'versatile', 'scheme']),
  value: z.number().int().nonnegative(),
  boost: z.number().int().nonnegative(),
  qty: z.number().int().positive(),
  text: z.string(),
  timing: z.array(z.enum(['immediate', 'during', 'after'])).optional(),
  image: z.string().nullable().optional(),
  effects: z
    .object({
      basic: z.string().nullable().optional(),
      immediate: z.string().nullable().optional(),
      during: z.string().nullable().optional(),
      after: z.string().nullable().optional(),
    })
    .optional(),
});

export const deckSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  cards: z.array(cardSchema).min(1),
});

export const sidekickSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  hp: z.number().int().positive(),
  move: z.number().int().positive(),
  attack: z.enum(['melee', 'ranged']),
  count: z.number().int().positive(),
  icon: z.string().optional(),
});

export const heroSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  hp: z.number().int().positive(),
  move: z.number().int().positive(),
  attack: z.enum(['melee', 'ranged']),
  deckId: z.string().min(1),
  sidekicks: z.array(sidekickSchema).optional(),
  icon: z.string().optional(),
  blurb: z.string().optional(),
  set: z.string().nullable().optional(),
  special: z.string().nullable().optional(),
});
